<template>
    <BtnMenu
        ref="filtersMenu"
        variant="outlined"
        :color="filterTypeCount ? 'primary' : undefined"
        size="sm"
        right-icon="funnel"
        :label="btnLabel"
        :menu-flip="false"
        class="!font-normal bg-white"
    >
        <template #menu="{ isSheet, hide }">
            <div
                :class="['my-2 flex flex-col justify-between', { 'py-6 max-w-lg': !isSheet }]"
                @click.stop
            >
                <div
                    :class="[
                        'flex flex-row border-solid border-slate-200',
                        isSheet ? 'border-b py-4 px-sm' : 'border-b-0 py-0 px-7',
                    ]"
                >
                    <Btn
                        v-if="isSheet"
                        icon="chevron-left"
                        compact
                        @click="hide()"
                    />
                    <h3
                        :class="['flex-grow font-subtitle-3', isSheet ? 'text-center' : 'text-left']"
                        v-text="t('documents.filtering.header')"
                    ></h3>
                    <Btn
                        variant="text"
                        color="primary"
                        compact
                        :label="t('documents.filtering.label_clear')"
                        class="!text-sm"
                        @click="
                            resetFilters();
                            submitSearch();
                            hide();
                        "
                    />
                </div>
                <div :class="['flex flex-col h-full pb-12', isSheet ? 'px-sm pt-9' : 'px-7 pt-8 overflow-visible']">
                    <div class="relative mb-9">
                        <input
                            v-model="filters.phrase"
                            :placeholder="t('documents.filtering.input_placeholder')"
                            @keydown.enter="
                                submitSearch();
                                hide();
                            "
                        />
                        <span
                            class="absolute leading-none cursor-pointer btn-search"
                            style="right: 1rem; top: 0.8rem"
                        >
                            <i class="icon icon-search"></i>
                        </span>
                    </div>
                    <div>
                        <div class="flex flex-row items-center mb-4">
                            <h4 class="flex flex-row items-center font-body-1-medium">
                                <i class="w-6 h-6 mr-4 icon icon-document"></i>{{ t('documents.filtering.label_type') }}
                            </h4>
                        </div>
                        <InputDocumentType
                            v-model="filters.documentTypes"
                            :totals="documentTypeTotals"
                            with-all
                            class="mb-6"
                        />
                    </div>
                    <div v-if="availableSemesters.length > 0">
                        <div class="flex flex-row items-center mb-7">
                            <h4 class="flex flex-row items-center font-body-1-medium">
                                <i class="w-6 h-6 mr-4 icon icon-clock"></i>
                                {{ t('documents.filtering.label_semester') }}
                            </h4>
                        </div>

                        <InputRange
                            v-model="filters.semester"
                            class="semester-slider"
                            :data="orderedSemsters"
                            data-value="id"
                            data-label="name"
                            :enable-cross="false"
                        />
                    </div>
                </div>
                <div :class="['flex flex-row', isSheet ? 'px-sm pb-sm' : 'px-7']">
                    <!-- native modifier has been removed, please confirm whether the function has been affected  -->
                    <BtnCta
                        class="w-full"
                        :label="t('documents.filtering.btn_filter')"
                        @click="
                            submitSearch();
                            hide();
                        "
                    />
                </div>
            </div>
        </template>
    </BtnMenu>
</template>

<script>
import InputDocumentType from '@components/InputDocumentType';
import InputRange from '@components/InputRange';
import { mapGetters } from 'vuex';
import { orderBy } from 'lodash-es';
import { useSemesters } from '@/queries/platformQueries';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'CourseShowDocumentsFilter',
    components: {
        InputDocumentType,
        InputRange,
    },
    emits: ['changed'],

    setup() {
        const { t } = useTranslation('course');

        const { data: availableSemesters, isLoading: loadingSemesters } = useSemesters();

        return {
            availableSemesters,
            loadingSemesters,
            t,
        };
    },
    data() {
        return {
            filters: {
                phrase: null,
                documentTypes: [],
                semester: [0, 0],
            },
            filtersOpen: false,
        };
    },

    computed: {
        ...mapGetters('course', ['documentTypeTotals']),
        ...mapGetters('ui', ['screen']),
        btnLabel() {
            return this.screen.maxSM
                ? ''
                : `${this.filterTypeCount ? `${this.filterTypeCount} ` : ''}${this.t(
                      'documents.filtering.btn_toggle',
                  )}`;
        },
        orderedSemsters() {
            return orderBy(this.availableSemesters, ['id'], ['asc']);
        },
        oldestSemester() {
            return this.orderedSemsters[0]?.id ?? 0;
        },
        newestSemester() {
            if (!this.orderedSemsters.length) return 0;
            return this.orderedSemsters[this.orderedSemsters.length - 1]?.id ?? 0;
        },
        filterTypeCount() {
            const hasSemesterFilter =
                this.filters.semester[0] !== this.oldestSemester || this.filters.semester[1] !== this.newestSemester;
            return this.filters.documentTypes.length + (hasSemesterFilter ? 1 : 0);
        },
    },

    watch: {
        oldestSemester: function oldestSemesterWatcher(newVal) {
            this.filters.semester[0] = newVal;
        },
        newestSemester: function newestSemesterWatcher(newVal) {
            this.filters.semester[1] = newVal;
        },
    },

    created() {
        this.$store.dispatch('study/initialize');
    },

    methods: {
        resetFilters() {
            this.filters.phrase = null;
            this.filters.documentTypes = [];
            this.filters.semester[0] = this.oldestSemester;
            this.filters.semester[1] = this.newestSemester;
        },
        getFilters() {
            return {
                phrase: this.filters.phrase,
                document_type: this.filters.documentTypes,
                min_semester: this.filters.semester[0],
                max_semester: this.filters.semester[1],
            };
        },
        submitSearch() {
            this.$emit('changed', this.getFilters());
        },
    },
};
</script>
